import React, { useState, useCallback, useEffect, useMemo, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MountainSnow, Plus, Minus, LocateFixed, Expand, Sparkles, Grid2x2, X } from "lucide-react";
import { AtlasMap, type AtlasMapControls } from "../components/map/AtlasMap";
import { MapInspector } from "../components/map/MapInspector";
import { FilterBar, type FilterType } from "../components/map/FilterBar";
import { MapLayersSheet } from "../components/map/MapLayersSheet";
import { MobileMapSearchBubble, MobileMapSearchField } from "../components/map/MobileMapSearch";
import { MapTracesLoadingChip } from "../components/map/MapTracesLoadingChip";
import { useMapFeatures } from "../hooks/useMapFeatures";
import { useMediaQuery } from "../hooks/useMediaQuery";
import { fetchCollections, fetchCollectionMap } from "../api/collections";
import { fetchRegion } from "../api/regions";
import { fetchTrail } from "../api/trails";
import { fetchProfileStats } from "../api/profile";
import { fetchShowcaseUrl } from "../api/showcase";
import {
  fetchExploredCells,
  fetchMapLayers,
  updateMapLayers,
  MAP_LAYERS_QUERY_KEY,
} from "../api/map";
import { hiddenCollectionIndexSet, isMapItemVisible } from "@atlas/shared";
import { computeFeatureCollectionBbox } from "../lib/geo";
import { LANDMARK_CATEGORIES } from "../lib/landmark-icons";
import type { MapFeatureProperties, MapFeatureCollection, MapSearchResult, HeatmapFeatureCollection } from "../api/map";

function CtrlBtn({
  icon,
  title,
  onClick,
  active,
}: {
  icon: React.ReactNode;
  title: string;
  onClick: () => void;
  /** Toggle buttons: pressed state (aria-pressed + accent tint). */
  active?: boolean;
}) {
  const idleColor = active ? "var(--text-accent)" : "var(--text-secondary)";
  return (
    <button
      onClick={onClick}
      title={title}
      aria-label={title}
      aria-pressed={active}
      style={{
        width: 38,
        height: 38,
        display: "grid",
        placeItems: "center",
        cursor: "pointer",
        background: "var(--surface-overlay)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        border: "none",
        borderBottom: "1px solid var(--border-default)",
        color: idleColor,
        transition: "var(--t-colors)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.color = active ? "var(--text-accent)" : "var(--text-primary)";
        e.currentTarget.style.background = "var(--surface-raised)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.color = idleColor;
        e.currentTarget.style.background = "var(--surface-overlay)";
      }}
    >
      {icon}
    </button>
  );
}

const EXPLORED_TOGGLE_KEY = "atlas:map:explored";

interface MapPageProps {
  staticFeatures?: MapFeatureCollection;
  staticHeatmap?: HeatmapFeatureCollection;
  initialFilter?: FilterType;
  collectionSlug?: string;
  regionSlug?: string;
  trailId?: string;
  /** Deep-link to a single feature: fly close to it and select it. */
  focusId?: string;
}

export default function MapPage({
  staticFeatures,
  staticHeatmap,
  initialFilter,
  collectionSlug,
  regionSlug,
  trailId,
  focusId,
}: MapPageProps = {}) {
  const isStatic = staticFeatures != null;
  const [filter, setFilter] = useState<FilterType>(initialFilter ?? "all");
  const [landmarkCategories, setLandmarkCategories] = useState<string[]>(LANDMARK_CATEGORIES);
  const [layersOpen, setLayersOpen] = useState(false);
  const [bbox, setBbox] = useState<[number, number, number, number] | null>(null);
  const [selected, setSelected] = useState<MapFeatureProperties | null>(null);
  const [collectionDismissed, setCollectionDismissed] = useState(false);
  const [regionDismissed, setRegionDismissed] = useState(false);
  const mapControlsRef = useRef<AtlasMapControls | null>(null);

  // Mobile mirrors the app's explore screen: full-height map, search bubble +
  // filter chips in one top row, detail in a bottom sheet only on selection.
  const isMobile = useMediaQuery("(max-width: 860px)");
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  // A fresh deep link (new slug/id) always starts un-dismissed.
  useEffect(() => setCollectionDismissed(false), [collectionSlug]);
  useEffect(() => setRegionDismissed(false), [regionSlug]);

  const collectionActive = !!collectionSlug && !collectionDismissed;
  const regionActive = !!regionSlug && !regionDismissed;

  const { features: rawLiveFeatures, heatmap: liveHeatmap, tracesLoading } = useMapFeatures(bbox, !isStatic);

  // Map layers (ATL-106): per-collection visibility. The exclusion list is
  // server-stored (syncs across web/mobile); hiding is display-only and
  // applied here, client-side, so gaps/completed/fog and matching are
  // untouched. Multi-membership rule: an item hides only when ALL the
  // collections it belongs to are hidden.
  const queryClient = useQueryClient();
  const layersQuery = useQuery({
    queryKey: MAP_LAYERS_QUERY_KEY,
    queryFn: fetchMapLayers,
    enabled: !isStatic,
    staleTime: 60_000,
  });
  const hiddenSlugs = useMemo(() => new Set(layersQuery.data?.hidden ?? []), [layersQuery.data]);
  const layersMutation = useMutation({ mutationFn: updateMapLayers });
  const handleChangeHidden = useCallback(
    (next: string[]) => {
      // Optimistic: the map reflects the toggle immediately; last write wins
      // server-side, so rapid toggling settles on the final state.
      queryClient.setQueryData(MAP_LAYERS_QUERY_KEY, { hidden: next });
      layersMutation.mutate(next);
    },
    [queryClient, layersMutation],
  );

  const liveFeatures = useMemo(() => {
    if (!rawLiveFeatures || hiddenSlugs.size === 0) return rawLiveFeatures;
    const hiddenIdx = hiddenCollectionIndexSet(rawLiveFeatures.collections, hiddenSlugs);
    if (hiddenIdx.size === 0) return rawLiveFeatures;
    return {
      ...rawLiveFeatures,
      features: rawLiveFeatures.features.filter((f) => isMapItemVisible(f.properties.c, hiddenIdx)),
    };
  }, [rawLiveFeatures, hiddenSlugs]);

  // Sheet data: lookup + per-collection counts from the unfiltered payload,
  // so hiding a collection doesn't zero its own count.
  const layerCollections = rawLiveFeatures?.collections ?? [];
  const layerCounts = useMemo(() => {
    const counts = new Map<string, number>();
    const lookup = rawLiveFeatures?.collections;
    if (!lookup) return counts;
    for (const f of rawLiveFeatures.features) {
      for (const idx of f.properties.c ?? []) {
        const slug = lookup[idx]?.slug;
        if (slug) counts.set(slug, (counts.get(slug) ?? 0) + 1);
      }
    }
    return counts;
  }, [rawLiveFeatures]);
  const landmarkCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const f of rawLiveFeatures?.features ?? []) {
      if (f.properties.type !== "landmark") continue;
      const cat = f.properties.category ?? "";
      counts[cat] = (counts[cat] ?? 0) + 1;
    }
    return counts;
  }, [rawLiveFeatures]);
  const layersActive = hiddenSlugs.size > 0 || landmarkCategories.length < LANDMARK_CATEGORIES.length;

  // Equipped map theme (STO-4) — reuses the profile stats query the rest of
  // the app already fetches; no new endpoint. Skipped for static/preview maps.
  const profileStatsQuery = useQuery({
    queryKey: ["profile/stats"],
    queryFn: fetchProfileStats,
    enabled: !isStatic,
    staleTime: 60_000,
  });
  const mapTheme = profileStatsQuery.data?.equipped?.mapTheme;
  const traceStyle = profileStatsQuery.data?.equipped?.traceStyle;
  const equippedPin = profileStatsQuery.data?.equipped?.pin;

  // Explored areas (spec 13 fog of war). Off by default, remembered per
  // device; the cells query only runs while the layer is on. Not shown on
  // static/showcase maps (public surface, private data).
  const [showExplored, setShowExplored] = useState<boolean>(() => {
    try {
      return localStorage.getItem(EXPLORED_TOGGLE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const toggleExplored = useCallback(() => {
    setShowExplored((v) => {
      const next = !v;
      try {
        localStorage.setItem(EXPLORED_TOGGLE_KEY, next ? "1" : "0");
      } catch {
        /* private mode etc. - toggle still works for the session */
      }
      return next;
    });
  }, []);
  const exploredQuery = useQuery({
    queryKey: ["map", "explored"],
    queryFn: () => fetchExploredCells(),
    enabled: !isStatic && showExplored,
    staleTime: 5 * 60_000,
  });
  const exploredCells = !isStatic && showExplored ? exploredQuery.data?.cells ?? null : null;

  const collectionsListQuery = useQuery({
    queryKey: ["collections"],
    queryFn: fetchCollections,
    enabled: !!collectionSlug,
    staleTime: 60_000,
  });
  const collectionMeta = collectionsListQuery.data?.collections.find((c) => c.slug === collectionSlug) ?? null;

  const collectionMapQuery = useQuery({
    queryKey: ["collections", collectionSlug, "map"],
    queryFn: () => fetchCollectionMap(collectionSlug!),
    enabled: collectionActive,
  });

  const regionQuery = useQuery({
    queryKey: ["regions", regionSlug],
    queryFn: () => fetchRegion(regionSlug!),
    enabled: !!regionSlug,
  });

  const trailQuery = useQuery({
    queryKey: ["trails", trailId],
    queryFn: () => fetchTrail(trailId!),
    enabled: !!trailId,
  });

  const features = staticFeatures ?? (collectionActive ? collectionMapQuery.data ?? null : liveFeatures);
  const heatmap = staticHeatmap ?? liveHeatmap;

  const regionOverlay = regionActive
    ? regionQuery.data?.geojson ?? null
    : collectionActive
      ? collectionMapQuery.data ?? null
      : null;

  const deepLinkBbox = useMemo(() => {
    // When focusing a single feature we fly straight to it (see effect below),
    // so skip the whole-collection fit that would otherwise fight the fly-to.
    // Region still fits first so its bbox-loaded live features arrive, then we
    // fly in once the target feature is present.
    if (collectionActive && collectionMapQuery.data && !focusId) return computeFeatureCollectionBbox(collectionMapQuery.data);
    if (regionActive && regionQuery.data?.geojson) return computeFeatureCollectionBbox(regionQuery.data.geojson);
    if (trailId && trailQuery.data?.geojson) return computeFeatureCollectionBbox(trailQuery.data.geojson);
    return null;
  }, [collectionActive, collectionMapQuery.data, regionActive, regionQuery.data, trailId, trailQuery.data, focusId]);

  // Deep-link focus (STO/nav): once the target feature is loaded, select it and
  // fly close to it. Runs once per focusId — guarded so panning the map (which
  // reloads live features) doesn't yank the view back.
  const focusedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!focusId || !features) return;
    if (focusedRef.current === focusId) return;
    const raw = features.features.find((x) => x.properties.id === focusId);
    if (!raw) return;
    setSelected(raw.properties);
    if (raw.geometry.type === "Point") {
      const [lng, lat] = raw.geometry.coordinates as [number, number];
      if (mapControlsRef.current) {
        mapControlsRef.current.flyTo([lng, lat], 15);
        focusedRef.current = focusId;
      }
    } else {
      focusedRef.current = focusId;
    }
  }, [focusId, features]);

  // Fly to and preselect the trail once its data lands.
  useEffect(() => {
    if (!trailId || !trailQuery.data) return;
    const t = trailQuery.data.trail;
    setSelected({
      id: t.id,
      name: t.name,
      slug: t.slug,
      type: "trail",
      status: t.completionPct >= 100 ? "collected" : t.completionPct > 0 ? "in_progress" : "not_collected",
      distanceKm: t.distanceKm ?? undefined,
    });
  }, [trailId, trailQuery.data]);

  const handleBoundsChange = useCallback((b: [number, number, number, number]) => {
    setBbox(b);
  }, []);

  const handleFeatureSelect = useCallback((f: MapFeatureProperties) => {
    setSelected(f);
  }, []);

  // Global search hit: select it and fly there. The result may be far outside
  // the current viewport — the bbox reload after the fly-to brings in its
  // geometry (trails get a wider zoom so the line isn't off-screen).
  const handleSearchSelect = useCallback((r: MapSearchResult) => {
    const { center, ...props } = r;
    setSelected(props);
    mapControlsRef.current?.flyTo(center, r.type === "trail" ? 11 : 14);
  }, []);

  const handleFocusMap = useCallback(
    (f: MapFeatureProperties) => {
      const raw = features?.features.find((x) => x.properties.id === f.id);
      if (raw && raw.geometry.type === "Point") {
        const [lng, lat] = raw.geometry.coordinates as [number, number];
        mapControlsRef.current?.flyTo([lng, lat], 15);
      }
    },
    [features],
  );

  const contextPill = collectionActive && collectionMeta
    ? {
        label: `${collectionMeta.name} · ${collectionMeta.completedCount}/${collectionMeta.itemCount}`,
        onDismiss: () => setCollectionDismissed(true),
      }
    : regionActive && regionQuery.data
      ? {
          label: `${regionQuery.data.name} · ${regionQuery.data.coveragePct.toFixed(0)}% explored`,
          onDismiss: () => setRegionDismissed(true),
        }
      : null;

  return (
    <div className="map-layout" style={{ display: "flex", flex: 1, minHeight: 0, overflow: "hidden", position: "relative" }}>
      {/* Map canvas */}
      <div style={{ flex: 1, minWidth: 0, position: "relative", overflow: "hidden" }}>
        <AtlasMap
          features={features}
          heatmap={heatmap}
          traceAboveTrails
          selectedId={selected?.id ?? null}
          filter={filter}
          landmarkCategories={landmarkCategories}
          onFeatureSelect={handleFeatureSelect}
          onBoundsChange={handleBoundsChange}
          controlsRef={mapControlsRef}
          fitBounds={deepLinkBbox}
          regionOverlay={regionOverlay}
          exploredCells={exploredCells}
          mapTheme={mapTheme ? { heatRamp: mapTheme.metadata.heatRamp, traceColor: mapTheme.metadata.traceColor } : null}
          traceStyle={
            traceStyle
              ? { dashArray: traceStyle.metadata.dashArray, glow: traceStyle.metadata.glow }
              : null
          }
          collectedPinColor={equippedPin?.metadata.color ?? null}
          showUserLocation={!isStatic}
        />

        {/* TOP overlays. Desktop: context pill + filter tabs. Mobile mirrors
            the app's explore screen: search bubble + filter chips in one row
            (the plain "Explore" pill is dropped; a dismissible deep-link scope
            pill still shows). */}
        <div
          style={{
            position: "absolute",
            top: isMobile ? 12 : 16,
            left: isMobile ? 12 : 16,
            right: isMobile ? 12 : 16,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: isMobile ? 8 : 12,
            pointerEvents: "none",
            zIndex: 10,
          }}
        >
          {isMobile && mobileSearchOpen && !isStatic ? (
            <MobileMapSearchField
              onClose={() => setMobileSearchOpen(false)}
              onSelect={(r) => {
                setMobileSearchOpen(false);
                handleSearchSelect(r);
              }}
            />
          ) : (
            <>
              {isMobile && !isStatic && (
                <MobileMapSearchBubble onOpen={() => setMobileSearchOpen(true)} />
              )}

              {/* Context pill: default "Explore" label (desktop only), or a
                  dismissible deep-link scope */}
              {contextPill ? (
                <div
                  className="atlas-glass"
                  style={{
                    pointerEvents: "auto",
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    minWidth: 0,
                    padding: "8px 8px 8px 14px",
                    borderRadius: "var(--radius-pill)",
                    boxShadow: "var(--shadow-md)",
                  }}
                >
                  <MountainSnow size={16} color="var(--sky-400)" style={{ flex: "0 0 auto" }} />
                  <span
                    style={{
                      fontSize: 13.5,
                      fontWeight: 700,
                      color: "var(--text-primary)",
                      minWidth: 0,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {contextPill.label}
                  </span>
                  <button
                    onClick={contextPill.onDismiss}
                    title="Clear map scope"
                    aria-label="Clear map scope"
                    style={{
                      width: 22,
                      height: 22,
                      flex: "0 0 auto",
                      display: "grid",
                      placeItems: "center",
                      cursor: "pointer",
                      border: "none",
                      borderRadius: "var(--radius-pill)",
                      background: "var(--surface-sunken)",
                      color: "var(--text-muted)",
                    }}
                  >
                    <X size={13} />
                  </button>
                </div>
              ) : (
                !isMobile && (
                  <div
                    className="atlas-glass"
                    style={{
                      pointerEvents: "auto",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "8px 14px",
                      borderRadius: "var(--radius-pill)",
                      boxShadow: "var(--shadow-md)",
                    }}
                  >
                    <MountainSnow size={16} color="var(--gold-400)" />
                    <span style={{ fontSize: 13.5, fontWeight: 700, color: "var(--text-primary)" }}>
                      Explore
                    </span>
                  </div>
                )
              )}

              {/* Filter tabs (FilterBar scrolls its own pill row so the category
                  dropdown isn't clipped by an overflow wrapper) */}
              <div style={{ pointerEvents: "auto", minWidth: 0, flex: isMobile ? 1 : undefined, position: "relative" }}>
                <FilterBar
                  filter={filter}
                  onChange={setFilter}
                  features={features}
                  landmarkCategories={landmarkCategories}
                  onOpenLayers={isStatic ? undefined : () => setLayersOpen((v) => !v)}
                  layersActive={layersActive}
                />
                {layersOpen && !isStatic && (
                  <MapLayersSheet
                    collections={layerCollections}
                    counts={layerCounts}
                    hidden={hiddenSlugs}
                    onChangeHidden={handleChangeHidden}
                    landmarkCategories={landmarkCategories}
                    onLandmarkCategoriesChange={setLandmarkCategories}
                    landmarkCounts={landmarkCounts}
                    onClose={() => setLayersOpen(false)}
                  />
                )}
              </div>
            </>
          )}
        </div>

        {/* First load of the user's traces: a quiet chip under the top row. */}
        {!isStatic && tracesLoading && <MapTracesLoadingChip top={isMobile ? 62 : 68} />}

        {/* LEGEND bottom-left */}
        <div
          style={{
            position: "absolute",
            left: 16,
            bottom: 16,
            display: "flex",
            flexDirection: "column",
            gap: 7,
            padding: "11px 13px",
            borderRadius: "var(--radius-md)",
            background: "var(--surface-overlay)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            border: "1px solid var(--border-default)",
            boxShadow: "var(--shadow-md)",
            zIndex: 10,
          }}
        >
          <div className="eyebrow" style={{ fontSize: 9, marginBottom: 1 }}>
            Legend
          </div>
          {[
            ["var(--accent)", "Collected"],
            ["var(--status-info)", "In progress"],
            ["rgba(86,103,119,0.7)", "Not visited"],
          ].map(([color, label]) => (
            <div key={label} style={{ display: "flex", alignItems: "center", gap: 9 }}>
              <span
                style={{
                  width: 11,
                  height: 11,
                  borderRadius: "50%",
                  background: color,
                  boxShadow: color === "var(--accent)" ? "0 0 6px rgba(244,183,64,0.55)" : "none",
                }}
              />
              <span
                style={{
                  fontSize: 11.5,
                  color: "var(--text-secondary)",
                  fontFamily: "var(--font-mono)",
                }}
              >
                {label}
              </span>
            </div>
          ))}
        </div>

        {/* ZOOM CONTROLS bottom-right */}
        <div
          style={{
            position: "absolute",
            right: 16,
            bottom: 16,
            display: "flex",
            flexDirection: "column",
            borderRadius: "var(--radius-md)",
            overflow: "hidden",
            border: "1px solid var(--border-default)",
            boxShadow: "var(--shadow-md)",
            zIndex: 10,
          }}
        >
          {/* Pinch covers zoom on touch screens — dropping these on mobile
              matches the app and thins the overlay stack. */}
          {!isMobile && (
            <>
              <CtrlBtn icon={<Plus size={17} />} title="Zoom in" onClick={() => mapControlsRef.current?.zoom(1)} />
              <CtrlBtn icon={<Minus size={17} />} title="Zoom out" onClick={() => mapControlsRef.current?.zoom(-1)} />
            </>
          )}
          {!isStatic && (
            <CtrlBtn icon={<LocateFixed size={17} />} title="My location" onClick={() => mapControlsRef.current?.locate()} />
          )}
          <CtrlBtn icon={<Expand size={17} />} title="Reset view" onClick={() => mapControlsRef.current?.reset()} />
          {!isStatic && (
            <CtrlBtn
              icon={<Grid2x2 size={17} />}
              title={showExplored ? "Hide explored areas" : "Show explored areas"}
              active={showExplored}
              onClick={toggleExplored}
            />
          )}
          {!isStatic && (
            <CtrlBtn
              icon={<Sparkles size={17} />}
              title="Open showcase"
              onClick={() => {
                // Navigates to the real public URL (not an app route) so the
                // destination is the exact link the user can bookmark/share.
                void fetchShowcaseUrl().then(({ url }) => window.location.assign(url));
              }}
            />
          )}
        </div>
      </div>

      {/* Inspector: permanent 360px rail on desktop; on mobile a bottom sheet
          (styled in appshell.css) that only exists while a feature is selected,
          so the map keeps the full height — app-explore-screen parity. */}
      {(!isMobile || selected != null) && (
        <MapInspector
          selected={selected}
          features={features}
          filter={filter}
          landmarkCategories={landmarkCategories}
          onSelect={handleFeatureSelect}
          onDeselect={() => setSelected(null)}
          onFocusMap={handleFocusMap}
          onSearchSelect={isStatic ? undefined : handleSearchSelect}
          regionName={regionActive ? regionQuery.data?.name : undefined}
          regionCoveragePct={regionActive ? regionQuery.data?.coveragePct : undefined}
          sheet={isMobile}
        />
      )}
    </div>
  );
}
